// Generated by GitHub Copilot
import { Component, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { OrbitControls, Stars } from "@react-three/drei";
import { Box3, Color, MeshStandardMaterial, SRGBColorSpace, TextureLoader, Vector3 } from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { motion, useReducedMotion } from "framer-motion";

const DEFAULT_ROLES = ["Data Scientist", "ML Engineer", "GenAI Builder"];
const DRAGON_MODEL_PATH = `${import.meta.env.BASE_URL}asset/Dragon/Dragon.obj`;
const DRAGON_TEXTURE_PATH = `${import.meta.env.BASE_URL}asset/Dragon/Dragon.jpg`;
const DRAGON_STATIC_PREVIEW_PATH = `${import.meta.env.BASE_URL}asset/Dragon/Dragon.jpg`;

const DRAGON_ACTIONS = {
  idle: {
    spinSpeed: 0.18,
    floatSpeed: 0.9,
    floatAmplitude: 0.03,
    glow: 0.12,
    scale: 1,
  },
  glide: {
    spinSpeed: 0.28,
    floatSpeed: 1.15,
    floatAmplitude: 0.06,
    glow: 0.18,
    scale: 1.04,
  },
  roar: {
    spinSpeed: 0.36,
    floatSpeed: 1.45,
    floatAmplitude: 0.1,
    glow: 0.26,
    scale: 1.1,
  },
};

/**
 * Clamp a numeric value within the provided min and max range.
 *
 * @param {number} value Numeric value.
 * @param {number} min Minimum value.
 * @param {number} max Maximum value.
 * @returns {number} Clamped value.
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Determine dragon action state from page scroll progress.
 *
 * @param {number} scrollProgress Scroll progress from 0 to 1.
 * @returns {"idle" | "glide" | "roar"} Dragon action key.
 */
function getDragonAction(scrollProgress) {
  if (scrollProgress >= 0.72) {
    return "roar";
  }

  if (scrollProgress >= 0.34) {
    return "glide";
  }

  return "idle";
}

/**
 * Return readable label text for the active dragon action.
 *
 * @param {"idle" | "glide" | "roar"} action Dragon action key.
 * @returns {string} Human-readable state label.
 */
function getDragonActionLabel(action) {
  if (action === "glide") {
    return "Sky Glide";
  }

  if (action === "roar") {
    return "Ember Roar";
  }

  return "Dragon Idle";
}

/**
 * Detect whether the current browser can initialize WebGL scenes.
 *
 * @returns {boolean} True when WebGL context creation is supported.
 */
function supportsWebglCanvas() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  try {
    const canvas = document.createElement("canvas");
    const context =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");

    return Boolean(context);
  } catch {
    return false;
  }
}

/**
 * Catch render-time failures from the 3D scene and trigger fallback UI.
 */
class HeroSceneErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    if (typeof this.props.onError === "function") {
      this.props.onError();
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

/**
 * Render an accessible static dragon visual when 3D rendering is unavailable.
 *
 * @param {{reasonLabel: string}} props Component props.
 * @returns {JSX.Element} Static fallback preview.
 */
function StaticDragonFallback({ reasonLabel }) {
  return (
    <div className="relative flex h-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_30%_26%,rgba(var(--accent-rgb),0.32),transparent_48%),radial-gradient(circle_at_76%_72%,rgba(var(--accent-secondary-rgb),0.26),transparent_50%)] px-5 py-6">
      <img
        src={DRAGON_STATIC_PREVIEW_PATH}
        alt="Dragon statue render preview"
        loading="lazy"
        className="h-full w-full rounded-2xl border border-[color:color-mix(in_srgb,var(--glass-border)_70%,transparent_30%)] object-cover object-center shadow-[0_18px_42px_color-mix(in_srgb,var(--glass-shadow)_72%,transparent_28%)]"
      />
      <p className="glass-chip pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full px-4 py-2 text-center text-xs text-[var(--text-primary)] md:text-sm">
        {reasonLabel}
      </p>
    </div>
  );
}

/**
 * Build a concise hero heading from long profile headlines.
 *
 * @param {string | undefined} rawHeadline Raw profile headline string.
 * @param {string | undefined} fallbackHeadline Fallback heading from static config.
 * @returns {string} Concise heading for stable hero layout.
 */
function getHeroHeadline(rawHeadline, fallbackHeadline) {
  const baseHeadline = typeof rawHeadline === "string" && rawHeadline.trim()
    ? rawHeadline.trim()
    : (fallbackHeadline || "").trim();

  if (!baseHeadline) {
    return "Data Scientist";
  }

  const compactSegments = baseHeadline
    .split("|")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (compactSegments.length >= 2) {
    return `${compactSegments[0]} | ${compactSegments[1]}`;
  }

  if (compactSegments.length === 1) {
    return compactSegments[0];
  }

  const words = baseHeadline.split(/\s+/).filter(Boolean);
  return words.slice(0, 8).join(" ");
}

/**
 * Render the uploaded dragon asset and animate it with scroll and pointer context.
 *
 * @param {{action: "idle" | "glide" | "roar", pointerBias: {x: number, y: number}, scrollProgress: number}} props Component props.
 * @returns {JSX.Element} Three.js mesh group.
 */
function HeroDragonAsset({ action, pointerBias, scrollProgress }) {
  const dragonRef = useRef(null);
  const dragonModel = useLoader(OBJLoader, DRAGON_MODEL_PATH);
  const dragonTexture = useLoader(TextureLoader, DRAGON_TEXTURE_PATH);

  const { dragonObject, dragonMaterials } = useMemo(() => {
    const texture = dragonTexture.clone();
    texture.colorSpace = SRGBColorSpace;
    texture.needsUpdate = true;

    const clone = dragonModel.clone(true);
    const materials = [];

    clone.traverse((node) => {
      if (node.isMesh) {
        const material = new MeshStandardMaterial({
          map: texture,
          roughness: 0.46,
          metalness: 0.2,
          color: new Color("#fff8ee"),
          emissive: new Color("#1a0904"),
          emissiveIntensity: DRAGON_ACTIONS.idle.glow,
        });

        node.material = material;
        node.castShadow = true;
        node.receiveShadow = true;
        materials.push(material);
      }
    });

    const bounds = new Box3().setFromObject(clone);
    const size = new Vector3();
    bounds.getSize(size);
    const maxDimension = Math.max(size.x, size.y, size.z) || 1;
    const normalizedScale = clamp(3 / maxDimension, 0.012, 0.45);
    clone.scale.setScalar(normalizedScale);

    bounds.setFromObject(clone);
    const center = new Vector3();
    bounds.getCenter(center);
    clone.position.set(-center.x, -center.y - 0.35, -center.z);

    return {
      dragonObject: clone,
      dragonMaterials: materials,
    };
  }, [dragonModel, dragonTexture]);

  useFrame((state) => {
    if (!dragonRef.current) {
      return;
    }

    const profile = DRAGON_ACTIONS[action] || DRAGON_ACTIONS.idle;
    const pointerX = clamp(pointerBias?.x || 0, -1, 1);
    const pointerY = clamp(pointerBias?.y || 0, -1, 1);
    const elapsed = state.clock.elapsedTime;

    const targetYaw = Math.PI * 0.55 + elapsed * profile.spinSpeed + pointerX * 0.28 + scrollProgress * 0.8;
    const targetPitch = 0.05 + pointerY * 0.16 + (action === "roar" ? 0.06 : 0);
    const targetRoll = Math.sin(elapsed * 0.55 + scrollProgress * 4) * 0.06;

    dragonRef.current.rotation.y += (targetYaw - dragonRef.current.rotation.y) * 0.09;
    dragonRef.current.rotation.x += (targetPitch - dragonRef.current.rotation.x) * 0.07;
    dragonRef.current.rotation.z += (targetRoll - dragonRef.current.rotation.z) * 0.07;

    const verticalBase = -0.08 + scrollProgress * 0.08;
    dragonRef.current.position.y +=
      (verticalBase + Math.sin(elapsed * profile.floatSpeed) * profile.floatAmplitude - dragonRef.current.position.y) *
      0.08;

    const targetScale = profile.scale + Math.abs(pointerX) * 0.04;
    dragonRef.current.scale.x += (targetScale - dragonRef.current.scale.x) * 0.08;
    dragonRef.current.scale.y += (targetScale - dragonRef.current.scale.y) * 0.08;
    dragonRef.current.scale.z += (targetScale - dragonRef.current.scale.z) * 0.08;

    for (const material of dragonMaterials) {
      const targetGlow = profile.glow + Math.abs(pointerX) * 0.05;
      material.emissiveIntensity += (targetGlow - material.emissiveIntensity) * 0.08;
    }
  });

  return (
    <group ref={dragonRef}>
      <primitive object={dragonObject} />
    </group>
  );
}

/**
 * Render the 3D hero canvas scene.
 *
 * @param {{action: "idle" | "glide" | "roar", pointerBias: {x: number, y: number}, scrollProgress: number}} props Component props.
 * @returns {JSX.Element} Canvas block.
 */
function HeroScene({ action, pointerBias, scrollProgress }) {
  const rotateSpeed = action === "roar" ? 1.3 : action === "glide" ? 0.95 : 0.7;

  return (
    <Canvas camera={{ position: [0, 0.65, 6.2], fov: 42 }}>
      <ambientLight intensity={1.05} />
      <hemisphereLight intensity={0.52} color="#ffd9c7" groundColor="#1a1010" />
      <directionalLight intensity={1.35} position={[3.5, 3.2, 4]} />
      <pointLight intensity={0.78} position={[-2, -1, 2]} color="#ff6b42" />
      <spotLight intensity={0.62} angle={0.5} penumbra={0.4} position={[0, 4, 2]} color="#ff7e5c" />
      <Stars radius={16} depth={35} count={700} factor={3} saturation={0.4} fade speed={0.7} />
      <Suspense fallback={null}>
        <HeroDragonAsset action={action} pointerBias={pointerBias} scrollProgress={scrollProgress} />
      </Suspense>
      <OrbitControls enablePan={false} enableZoom={false} autoRotate autoRotateSpeed={rotateSpeed} />
    </Canvas>
  );
}

/**
 * Render the portfolio hero section with 3D scene and typed role animation.
 *
 * @param {{config: Record<string, any>, profile: Record<string, any>, loading: boolean, error?: string}} props Component props.
 * @returns {JSX.Element} Hero section markup.
 */
function Hero({ config, profile, loading, error = "" }) {
  const reduceMotion = useReducedMotion();
  const roles = profile?.identity?.roles?.length
    ? profile.identity.roles
    : config?.roles?.length
      ? config.roles
      : DEFAULT_ROLES;
  const [roleIndex, setRoleIndex] = useState(0);
  const [typed, setTyped] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [pointerBias, setPointerBias] = useState({ x: 0, y: 0 });
  const [useGithubAvatarFallback, setUseGithubAvatarFallback] = useState(false);
  const [useStaticDragonFallback, setUseStaticDragonFallback] = useState(false);
  const canRenderWebgl = useMemo(() => supportsWebglCanvas(), []);

  useEffect(() => {
    const full = roles[roleIndex % roles.length];
    let charIndex = 0;

    const interval = setInterval(() => {
      charIndex += 1;
      setTyped(full.slice(0, charIndex));
      if (charIndex >= full.length) {
        clearInterval(interval);
        setTimeout(() => {
          setRoleIndex((prev) => (prev + 1) % roles.length);
          setTyped("");
        }, 1200);
      }
    }, 70);

    return () => clearInterval(interval);
  }, [roleIndex, roles]);

  const localAvatarUrl = `${import.meta.env.BASE_URL}asset/venkat.dhulipudi.jpg`;
  const githubAvatarUrl = profile?.github?.profile?.avatarUrl?.trim() || "";
  const preferredAvatarUrl = profile?.identity?.avatarUrl?.trim() || localAvatarUrl;

  useEffect(() => {
    setUseGithubAvatarFallback(false);
  }, [preferredAvatarUrl, githubAvatarUrl]);

  useEffect(() => {
    let frame = 0;

    /**
     * Track page scroll and normalize it for animation state transitions.
     */
    function syncScrollProgress() {
      cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const nextProgress = maxScroll > 0 ? window.scrollY / maxScroll : 0;
        setScrollProgress(clamp(nextProgress, 0, 1));
      });
    }

    syncScrollProgress();
    window.addEventListener("scroll", syncScrollProgress, { passive: true });
    window.addEventListener("resize", syncScrollProgress);

    return () => {
      window.removeEventListener("scroll", syncScrollProgress);
      window.removeEventListener("resize", syncScrollProgress);
      cancelAnimationFrame(frame);
    };
  }, []);

  const displayName = profile?.identity?.name || config?.name;
  const displayPronouns = profile?.identity?.pronouns || "";
  const displayAvatarUrl = useGithubAvatarFallback
    ? githubAvatarUrl
    : preferredAvatarUrl || githubAvatarUrl;
  const sourceHeadline = profile?.identity?.headline || config?.heading;
  const displayHeadline = getHeroHeadline(sourceHeadline, config?.heading);
  const displaySummary = profile?.identity?.summary || config?.summary;
  const displayLocation = profile?.identity?.location || config?.location || "Greater Liverpool Area, United Kingdom";
  const heroAchievements = Array.isArray(config?.achievements)
    ? config.achievements
      .filter((item) => item?.value && item?.label)
      .slice(0, 4)
    : [];
  const githubUrl = profile?.contact?.github || "https://github.com/Ramdragneel01";
  const mediumUrl = profile?.contact?.medium || "https://medium.com/@RamPrakashD";
  const dragonAction = getDragonAction(scrollProgress);
  const actionLabel = getDragonActionLabel(dragonAction);
  const shouldRenderStaticDragon = reduceMotion || useStaticDragonFallback || !canRenderWebgl;
  const staticDragonReasonLabel = reduceMotion
    ? "3D mode reduced for accessibility preferences."
    : !canRenderWebgl
      ? "3D preview is unavailable on this device. Showing static dragon."
      : "3D preview is temporarily unavailable. Showing static dragon.";
  const githubMetricTooltip = typeof error === "string" && error.trim() ? error.trim() : "";
  const headlineTooltip =
    typeof sourceHeadline === "string" && sourceHeadline.trim() && sourceHeadline.trim() !== displayHeadline
      ? sourceHeadline.trim()
      : "";

  /**
   * Map pointer coordinates from the hero scene card into normalized x/y bias values.
   *
   * @param {number} clientX Pointer X coordinate.
   * @param {number} clientY Pointer Y coordinate.
   * @param {HTMLElement} element Target element bounds reference.
   */
  function updatePointerBias(clientX, clientY, element) {
    const bounds = element.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
      return;
    }

    const normalizedX = ((clientX - bounds.left) / bounds.width) * 2 - 1;
    const normalizedY = ((clientY - bounds.top) / bounds.height) * 2 - 1;
    setPointerBias({
      x: clamp(normalizedX, -1, 1),
      y: clamp(normalizedY, -1, 1),
    });
  }

  /**
   * Handle pointer movement over the scene card to steer dragon motion.
   *
   * @param {React.PointerEvent} event Pointer event.
   */
  function handleScenePointerMove(event) {
    updatePointerBias(event.clientX, event.clientY, event.currentTarget);
  }

  /**
   * Reset scene pointer influence when pointer exits the hero card.
   */
  function handleScenePointerLeave() {
    setPointerBias({ x: 0, y: 0 });
  }

  return (
    <section className="relative overflow-hidden py-20 md:py-24" id="home">
      <div className="absolute -left-16 top-10 h-72 w-72 rounded-full bg-[#9bc1ff]/38 blur-3xl" />
      <div className="absolute -right-20 bottom-8 h-80 w-80 rounded-full bg-[#c2b0ff]/30 blur-3xl" />

      <div className="grid gap-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="glass-card-strong relative rounded-3xl p-8 md:p-10 lg:p-12"
        >
          <div className={displayAvatarUrl ? "grid gap-8 lg:grid-cols-[minmax(0,1fr)_17.5rem] lg:items-start" : "grid gap-8"}>
            <div>
              <p className="text-sm uppercase tracking-[0.25em] text-[rgba(var(--accent-rgb),0.95)]">{displayName}</p>
              {displayPronouns ? (
                <p className="mt-2 text-xs uppercase tracking-[0.16em] text-[var(--text-secondary)]">{displayPronouns}</p>
              ) : null}
              <div
                className={headlineTooltip ? "hero-headline-tooltip hero-headline-tooltip--enabled" : "hero-headline-tooltip"}
              >
                <h1
                  className="mt-3 break-words font-display text-4xl leading-[0.95] sm:text-5xl md:text-6xl"
                  aria-label={sourceHeadline}
                >
                  {displayHeadline}
                </h1>
                {headlineTooltip ? (
                  <span role="tooltip" className="hero-headline-tooltip__bubble">
                    {headlineTooltip}
                  </span>
                ) : null}
              </div>
              <p className="mt-4 text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)]">{displayLocation}</p>
              {loading ? (
                <div
                  className="mt-5 grid gap-2"
                  role="status"
                  aria-live="polite"
                  aria-label="Loading hero summary"
                  aria-busy="true"
                >
                  <div className="skeleton skeleton-text" style={{ width: "92%" }} />
                  <div className="skeleton skeleton-text" style={{ width: "84%" }} />
                  <div className="skeleton skeleton-text" style={{ width: "66%" }} />
                </div>
              ) : (
                <p className="mt-5 text-lg text-[var(--text-secondary)] md:text-xl">{displaySummary}</p>
              )}

              <div className="mt-6 h-9 text-lg text-[rgba(var(--accent-rgb),0.95)] md:text-xl" aria-live="polite">
                {typed}
                <span className="ml-1 inline-block h-6 w-[2px] animate-pulse bg-[rgba(var(--accent-rgb),0.9)] align-middle" />
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/projects"
                  className="glass-button-primary rounded-full px-5 py-2.5 text-sm font-semibold transition"
                >
                  Explore Projects
                </Link>
                <a
                  href={profile?.contact?.linkedin || "https://www.linkedin.com/in/ramprakashdhulipudi/"}
                  target="_blank"
                  rel="noreferrer"
                  className="glass-button-secondary rounded-full px-5 py-2.5 text-sm font-semibold"
                >
                  LinkedIn Profile
                </a>
                <a
                  href={githubUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="glass-button-secondary rounded-full px-5 py-2.5 text-sm font-semibold"
                >
                  GitHub
                </a>
                <a
                  href={mediumUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="glass-button-secondary rounded-full px-5 py-2.5 text-sm font-semibold"
                >
                  Medium
                </a>
              </div>

              {loading ? <p className="mt-4 text-sm text-[var(--text-secondary)]">Syncing live profile context...</p> : null}
            </div>

            {displayAvatarUrl ? (
              <div className="glass-card-soft mx-auto w-full max-w-[18rem] overflow-hidden rounded-3xl p-2 lg:mx-0 lg:justify-self-end">
                <img
                  src={displayAvatarUrl}
                  alt={`${displayName} portrait`}
                  loading="lazy"
                  className="h-[19rem] w-full rounded-[1.25rem] object-cover object-top sm:h-[22rem]"
                  onError={() => {
                    if (!useGithubAvatarFallback && githubAvatarUrl && displayAvatarUrl !== githubAvatarUrl) {
                      setUseGithubAvatarFallback(true);
                    }
                  }}
                />
              </div>
            ) : null}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ amount: 0.25, once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="glass-card-strong relative h-[320px] overflow-hidden rounded-3xl sm:h-[360px] md:h-[420px]"
          onPointerMove={handleScenePointerMove}
          onPointerLeave={handleScenePointerLeave}
        >
          <div
            className="glass-chip pointer-events-none absolute left-4 top-4 z-10 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--accent-rgb),0.95)]"
            aria-hidden="true"
          >
            {actionLabel}
          </div>

          {shouldRenderStaticDragon ? (
            <StaticDragonFallback reasonLabel={staticDragonReasonLabel} />
          ) : (
            <HeroSceneErrorBoundary
              fallback={<StaticDragonFallback reasonLabel="3D preview is temporarily unavailable. Showing static dragon." />}
              onError={() => {
                setUseStaticDragonFallback(true);
              }}
            >
              <HeroScene action={dragonAction} pointerBias={pointerBias} scrollProgress={scrollProgress} />
            </HeroSceneErrorBoundary>
          )}

          <p className="pointer-events-none absolute bottom-4 left-4 z-10 text-xs tracking-[0.04em] text-[var(--text-secondary)]">
            Just a happy dragon I made over the weekend for fun.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.2, once: true }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="glass-card mt-10 grid gap-4 rounded-2xl p-4 md:grid-cols-2"
      >
        {loading ? (
          <div
            className="grid gap-4 md:col-span-2 md:grid-cols-2"
            role="status"
            aria-live="polite"
            aria-label="Loading hero metrics"
            aria-busy="true"
          >
            <div className="grid gap-2">
              <div className="skeleton" style={{ width: 120, height: 12 }} />
              <div className="skeleton" style={{ width: 90, height: 28 }} />
              <div className="skeleton" style={{ width: 150, height: 12 }} />
            </div>
            <div className="grid gap-2">
              <div className="skeleton" style={{ width: 120, height: 12 }} />
              <div className="skeleton" style={{ width: 90, height: 28 }} />
              <div className="skeleton" style={{ width: 170, height: 12 }} />
            </div>
          </div>
        ) : (
          <>
            <div
              className={githubMetricTooltip ? "hero-metric-tooltip hero-metric-tooltip--enabled" : "hero-metric-tooltip"}
            >
              <div className="flex items-center gap-1.5">
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">GitHub</p>
                {githubMetricTooltip ? (
                  <button
                    type="button"
                    className="hero-metric-tooltip__trigger"
                    aria-label="Show GitHub profile data source status"
                    aria-describedby="github-metric-tooltip"
                  >
                    i
                  </button>
                ) : null}
              </div>
              <p className="mt-1 text-2xl font-semibold text-[rgba(var(--accent-rgb),0.95)]">
                {profile?.github?.profile?.publicRepos ?? 0}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">Public repositories</p>
              {githubMetricTooltip ? (
                <span id="github-metric-tooltip" role="tooltip" className="hero-metric-tooltip__bubble">
                  {githubMetricTooltip}
                </span>
              ) : null}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">Medium</p>
              <p className="mt-1 text-2xl font-semibold text-[rgba(var(--accent-rgb),0.95)]">
                {profile?.medium?.totalArticles ?? profile?.medium?.articles?.length ?? 0}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">Total published articles</p>
            </div>
          </>
        )}
      </motion.div>

      {heroAchievements.length ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2, once: true }}
          transition={{ duration: 0.6, delay: 0.16, ease: "easeOut" }}
          className="glass-card mt-6 rounded-2xl p-4"
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--accent-rgb),0.95)]">
              Highlighted Outcomes
            </p>
            <p className="text-xs text-[var(--text-secondary)]">Outcome-first snapshot</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {heroAchievements.map((achievement) => (
              <article key={`${achievement.label}-${achievement.value}`} className="glass-card-soft rounded-2xl p-4">
                <p className="text-2xl font-bold text-[rgba(var(--accent-rgb),0.95)]">{achievement.value}</p>
                <p className="mt-1 text-sm font-semibold text-[var(--text-primary)]">{achievement.label}</p>
                {achievement.evidence ? (
                  <p className="mt-2 text-xs text-[var(--text-secondary)]">{achievement.evidence}</p>
                ) : null}
                {achievement.projectUrl ? (
                  <a
                    href={achievement.projectUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="glass-button-secondary mt-3 inline-flex rounded-full px-3 py-1.5 text-xs font-semibold"
                  >
                    {achievement.projectLabel || "View project"}
                  </a>
                ) : null}
              </article>
            ))}
          </div>
        </motion.div>
      ) : null}
    </section>
  );
}

export default Hero;
